import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { differenceInMonths, format } from 'date-fns';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const getSeverityBadgeClass = (severity) => {
  if (!severity) return 'bg-slate-600 text-white';
  const s = String(severity).toLowerCase();
  if (s === 'severe') return 'bg-red-600 text-white';
  if (s === 'moderate') return 'bg-amber-600 text-white';
  if (s === 'minor') return 'bg-green-700 text-white';
  return 'bg-slate-600 text-white';
};

const getDecisionBadgeClass = (decision) => {
  if (!decision) return 'bg-slate-600 text-white';
  if (decision === 'non-blameworthy') return 'bg-green-700 text-white';
  if (decision === 'blameworthy') return 'bg-red-600 text-white';
  return 'bg-slate-600 text-white';
};

const getP2PStatus = (monthsSinceLastCompleted) => {
  if (monthsSinceLastCompleted === null) {
    return { label: 'Overdue', description: 'No completed P2P on record' };
  }

  // Boundary rule (per requirement):
  // - < 12 months: Compliant
  // - 12–23 months: Due
  // - >= 24 months: Overdue
  if (monthsSinceLastCompleted < 12) {
    return { label: 'Compliant', description: 'Last completed within 12 months' };
  }

  if (monthsSinceLastCompleted < 24) {
    return { label: 'Due', description: 'Last completed between 12 and 24 months ago' };
  }

  return { label: 'Overdue', description: 'Last completed over 24 months ago' };
};

const getP2PBadgeClass = (label) => {
  if (label === 'Compliant') return 'bg-green-700 text-white';
  if (label === 'Due') return 'bg-amber-600 text-white';
  return 'bg-red-600 text-white';
};

export default function DriverReport() {
  const { driverId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [accidents, setAccidents] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [driverRes, assessmentsRes, accidentsRes] = await Promise.all([
        axios.get(`${API}/drivers/${driverId}`, getAuthHeaders()),
        axios.get(`${API}/assessments?driver_id=${driverId}`, getAuthHeaders()),
        axios.get(`${API}/accidents?driver_id=${driverId}`, getAuthHeaders()),
      ]);

      setDriver(driverRes.data);
      setAssessments(assessmentsRes.data || []);
      setAccidents(accidentsRes.data || []);
    } catch (e) {
      toast.error('Failed to load driver report');
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedAccidents = useMemo(() => {
    // Chronological (oldest → newest)
    return [...accidents].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [accidents]);

  const sortedAssessments = useMemo(() => {
    const getSortDate = (a) => {
      if (a.status === 'completed' && a.completed_date) return new Date(a.completed_date);
      return new Date(a.scheduled_date);
    };

    // Chronological (oldest → newest)
    return [...assessments].sort((a, b) => getSortDate(a) - getSortDate(b));
  }, [assessments]);

  const p2pAssessments = useMemo(() => {
    return assessments.filter((a) => a.assessment_type === 'Yearly Peer-to-Peer Review');
  }, [assessments]);

  const lastCompletedP2P = useMemo(() => {
    const completed = p2pAssessments
      .filter((a) => a.status === 'completed' && a.completed_date)
      .sort((a, b) => new Date(b.completed_date) - new Date(a.completed_date));

    return completed[0] || null;
  }, [p2pAssessments]);

  const monthsSinceLastCompletedP2P = useMemo(() => {
    if (!lastCompletedP2P) return null;
    return Math.max(0, differenceInMonths(new Date(), new Date(lastCompletedP2P.completed_date)));
  }, [lastCompletedP2P]);

  const p2pStatus = useMemo(() => {
    return getP2PStatus(monthsSinceLastCompletedP2P);
  }, [monthsSinceLastCompletedP2P]);

  const latestRtc = sortedAccidents[sortedAccidents.length - 1] || null;

  const printNow = () => {
    window.print();
  };

  if (loading) return <div className="py-12 text-center">Loading report…</div>;
  if (!driver) return <div className="py-12 text-center">Driver not found</div>;

  return (
    <div className="space-y-6 print-container" data-testid="driver-report">
      <div className="flex items-start justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100">Driver Report</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">Generated {format(new Date(), 'PPP p')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
          <Button onClick={printNow}>Print</Button>
        </div>
      </div>

      {/* Printable header */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold">Driver Report</h1>
        <p className="text-xs">Generated {format(new Date(), 'PPP p')}</p>
      </div>

      <Card className="print:avoid-page-break">
        <CardHeader>
          <CardTitle className="text-base">Driver</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-slate-500">Name</p>
            <p className="font-medium text-slate-800 dark:text-slate-200">{driver.name}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Employee ID</p>
            <p className="font-medium text-slate-800 dark:text-slate-200">{driver.employee_id || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Status</p>
            <Badge variant={driver.status === 'active' ? 'default' : 'secondary'}>{driver.status}</Badge>
          </div>
          {driver.hire_date && (
            <div>
              <p className="text-sm text-slate-500">Hire date</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{format(new Date(driver.hire_date), 'PPP')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="print:avoid-page-break">
        <CardHeader>
          <CardTitle className="text-base">P2P Compliance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={getP2PBadgeClass(p2pStatus.label)}>{p2pStatus.label}</Badge>
            <span className="text-sm text-slate-600 dark:text-slate-400">{p2pStatus.description}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500">Completed P2P count</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">
                {p2pAssessments.filter((a) => a.status === 'completed').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Last completed P2P</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">
                {lastCompletedP2P?.completed_date ? format(new Date(lastCompletedP2P.completed_date), 'PPP') : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Months since last P2P</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">
                {monthsSinceLastCompletedP2P === null ? '—' : monthsSinceLastCompletedP2P}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="print:avoid-page-break">
        <CardHeader>
          <CardTitle className="text-base">Latest RTC</CardTitle>
        </CardHeader>
        <CardContent>
          {latestRtc ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="secondary">{format(new Date(latestRtc.date), 'PPP')}</Badge>
                <Badge className={getSeverityBadgeClass(latestRtc.severity)}>
                  {latestRtc.severity || 'Not set'}
                </Badge>
                <Badge className={getDecisionBadgeClass(latestRtc.blameworthy)}>
                  {latestRtc.blameworthy
                    ? latestRtc.blameworthy === 'non-blameworthy'
                      ? 'Non-Blameworthy'
                      : 'Blameworthy'
                    : 'Not set'}
                </Badge>
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-300">{latestRtc.description}</p>
            </div>
          ) : (
            <p className="text-slate-500">No RTC records</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="driver-report-section-header">
          <CardTitle className="text-base">RTC History</CardTitle>
        </CardHeader>
        <CardContent className="driver-report-section-content print:p-0">
          {sortedAccidents.length === 0 ? (
            <p className="text-slate-500">No RTC records</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b dark:border-slate-700">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Severity</th>
                    <th className="py-2 pr-3">Decision</th>
                    <th className="py-2 pr-3">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAccidents.map((a) => (
                    <tr key={a.id} className="border-b dark:border-slate-800 align-top">
                      <td className="py-2 pr-3 whitespace-nowrap">{format(new Date(a.date), 'PPP')}</td>
                      <td className="py-2 pr-3">
                        <Badge className={getSeverityBadgeClass(a.severity)}>
                          {a.severity || 'Not set'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge className={getDecisionBadgeClass(a.blameworthy)}>
                          {a.blameworthy
                            ? a.blameworthy === 'non-blameworthy'
                              ? 'Non-Blameworthy'
                              : 'Blameworthy'
                            : 'Not set'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">{a.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="driver-report-section-header">
          <CardTitle className="text-base">Assessments</CardTitle>
        </CardHeader>
        <CardContent className="driver-report-section-content print:p-0">
          {sortedAssessments.length === 0 ? (
            <p className="text-slate-500">No assessments</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b dark:border-slate-700">
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Course</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Scheduled</th>
                    <th className="py-2 pr-3">Completed</th>
                    <th className="py-2 pr-3">Assessor</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAssessments.map((a) => (
                    <tr key={a.id} className="border-b dark:border-slate-800 align-top">
                      <td className="py-2 pr-3">{a.assessment_type}</td>
                      <td className="py-2 pr-3">{a.course_title || '—'}</td>
                      <td className="py-2 pr-3">{a.status}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{a.scheduled_date ? format(new Date(a.scheduled_date), 'PPP p') : '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{a.completed_date ? format(new Date(a.completed_date), 'PPP') : '—'}</td>
                      <td className="py-2 pr-3">{a.assessor || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <style>{`
        @page { margin: 14mm; }

        @media print {
          body { background: white !important; }
          a { text-decoration: none; color: black; }

          /* Avoid splitting cards across pages */
          .print\\:avoid-page-break { break-inside: avoid; page-break-inside: avoid; }

          /* Keep table headers readable */
          th { color: black !important; }

          /* Reduce card chrome / spacing to help keep sections together */
          .print-container { padding: 0 !important; }
          [data-testid="driver-report"] { padding: 0 !important; }

          /* Cards */
          .print-container .border { box-shadow: none !important; }

          /* Prevent large whitespace around headers */
          .print-container h1 { margin-bottom: 6px !important; }

          /* Ensure section headers are not orphaned at end of page */
          .driver-report-section-header { break-after: avoid; page-break-after: avoid; }
          .driver-report-section-content { break-before: avoid; page-break-before: avoid; }

          /* Table: repeat header + avoid row splitting */
          thead { display: table-header-group; }
          tr { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
